import { ColumnState } from 'ag-grid-community';
import { CompanyDto } from 'src/dtos/company/companyDto';
import { UserResponseDto } from 'src/dtos/user/response/userResponseDto';
import { create } from 'zustand';
import { defaultPRD2010_R01Dto, PRD2010_R01Dto } from './Dto_PRD2010_R01';
import { DateRange } from 'react-day-picker';
import { showWarning } from 'src/utils/toast/toastUtil';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';

export interface PRD2010_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	closeTag: string;
}

export interface Store_PRD2010_R01 {
	PRD2010_R01Filter: PRD2010_R01Filter;
	PRD2010_R01PastFilter: PRD2010_R01Filter;
	setPRD2010_R01Filter: (filter: Partial<PRD2010_R01Filter>) => void;
	setPRD2010_R01PastFilter: (filter: Partial<PRD2010_R01Filter>) => void;

    PRD2010_R01List: PRD2010_R01Dto[];
    setPRD2010_R01List: (userList: PRD2010_R01Dto[]) => void;
    insertPRD2010_R01List: (data: PRD2010_R01Dto[], index?: number) => void;
    removePRD2010_R01List: (index: number) => void;
    updatePRD2010_R01List: (index: number, data: PRD2010_R01Dto) => void;
	updatePRD2010_R01Instructions: (valueIndex: number, instructions: string) => void;

    deletedPRD2010_R01List: string[];
    addDeletedPRD2010_R01List: (idList: string[]) => void;
    removeDeletedPRD2010_R01List: (idList?: string[]) => void;

    selectedPRD2010_R01List: string[];
	setSelectedPRD2010_R01List: (idList: string[]) => void;
    addSelectedPRD2010_R01List: (idList: string[]) => void;
    removeSelectedPRD2010_R01List: (idList?: string[]) => void;
	initSelectedPRD2010_R01List: () => void;
	allSelectedPRD2010_R01List: () => void;

	originalPRD2010_R01List: PRD2010_R01Dto[];
	setOriginalPRD2010_R01List: (list: PRD2010_R01Dto[]) => void;

    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;
	modalVisible: boolean;				setModalVisible: (modalVisible: boolean) => void;
	modalValueIndex: number;			setModalValueIndex: (valueIndex: number) => void;
	modalInstructions: string;			setModalInstructions: (instructions: string) => void;
	
	isDisabled: (data: PRD2010_R01Dto) => boolean;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

    initData: () => void;


}


export interface Store_PRD2010_R01_GridCalendarState {

	// ====== MST1020_R01 GridCalendar 상태 ======
	PRD2010_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
		/** compCd 편집 종료 시 orderDt 달력을 열기 위한 대기(valueIndex). 테이블에서 셀 위치 계산 후 null로 초기화 */
		gridCalendarTrigger: number | null;
	};
	setPRD2010_R01GridCalendarState: (state: Partial<Store_PRD2010_R01_GridCalendarState['PRD2010_R01GridCalendarState']>) => void;
	initPRD2010_R01GridCalendarState: () => void;

}

export const defaultPRD2010_R01Filter: PRD2010_R01Filter = {
	compCd: '',
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	workplaceCd: '',
	jobTeam: '',
	closeTag: 'N',
}

export const usePRD2010_R01Store = create<Store_PRD2010_R01>((set) => ({
	PRD2010_R01Filter: defaultPRD2010_R01Filter,
	PRD2010_R01PastFilter: defaultPRD2010_R01Filter,
	setPRD2010_R01PastFilter: (filter: Partial<PRD2010_R01Filter>) => set((state) => ({
		...state,
		PRD2010_R01PastFilter: { ...state.PRD2010_R01PastFilter, ...filter }
	})),
	setPRD2010_R01Filter: (filter: Partial<PRD2010_R01Filter>) => set((state) => ({
		...state,
		PRD2010_R01Filter: { ...state.PRD2010_R01Filter, ...filter }
	})),

    PRD2010_R01List: [],
    setPRD2010_R01List: (PRD2010_R01List: PRD2010_R01Dto[]) => set((state) => {
		let newList: PRD2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD2010_R01List.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultPRD2010_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
				disabled: state.isDisabled(item)
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			PRD2010_R01List: newList
		}
	}),
	insertPRD2010_R01List: (data: PRD2010_R01Dto[], index?: number) => set((state) => {
		let newPRD2010_R01List = [...state.PRD2010_R01List];

		let targetRowData = newPRD2010_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newPRD2010_R01List.map(item => item.valueIndex || 0)) + 1;

		// newSYS4100_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newPRD2010_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS4100_R01List에 대체
		newPRD2010_R01List = newPRD2010_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newPRD2010_R01List.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultPRD2010_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newPRD2010_R01List.splice(index, 0, ...newData);
		} else {
			newPRD2010_R01List.push(...newData);
		}		

		return {
			...state,
			PRD2010_R01List: newPRD2010_R01List
		}
	}),
	removePRD2010_R01List: (index: number) => set((state) => {
		const newPRD2010_R01List = [...state.PRD2010_R01List].filter((item) => item.valueIndex !== index);
		return {
			...state,
			PRD2010_R01List: newPRD2010_R01List
		}
	}),
	updatePRD2010_R01List: (valueIndex: number, data: PRD2010_R01Dto) => set((state) => {
		const _PRD2010_R01List = state.PRD2010_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		if(data.orderQty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.orderQty = 0;
		}
		return {
			...state,
			PRD2010_R01List: _PRD2010_R01List
		}
	}),
	updatePRD2010_R01Instructions: (valueIndex: number, instructions: string) => set((state) => {
		const _PRD2010_R01List = state.PRD2010_R01List.map((item) => {
			if (item.valueIndex !== valueIndex) return item;
			const originRow = state.originalPRD2010_R01List.find(
				(o) => o.productOrderNo === item.productOrderNo && item.productOrderNo !== ''
			);
			let isModified: boolean;
			if (item.inserted) {
				isModified = false;
			} else if (originRow) {
				// 이미 수정된 상태(isModified === true)라면 원래대로 되돌리지 않고 true 유지
				// 또는 기존 값과 다르면 true
				isModified = item.isModified || originRow.instructions !== instructions;
			} else {
				isModified = item.isModified || item.instructions !== instructions;
			}
			return { ...item, instructions, isModified };
		});
		return {
			...state,
			PRD2010_R01List: _PRD2010_R01List
		}
	}),

    deletedPRD2010_R01List: [],
    addDeletedPRD2010_R01List: (idList: string[]) => set((state) => {
        const _deletedUserList = [...state.deletedPRD2010_R01List];
		
		for(const valueIndex of idList) {
			const isInserted = state.PRD2010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.PRD2010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedUserList.push(valueIndex);
			}
		}

        return {
            ...state,
            deletedPRD2010_R01List: _deletedUserList
        }
    }),
    removeDeletedPRD2010_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        deletedPRD2010_R01List: [...state.deletedPRD2010_R01List].filter(value => idList ? !idList.includes(value) : false)
    })),

    selectedPRD2010_R01List: [],
	setSelectedPRD2010_R01List: (idList: string[]) => set({ selectedPRD2010_R01List: idList }),
	//add해도 set한거처럼 대체
    addSelectedPRD2010_R01List: (idList: string[]) => set((state) => {
        // const _selectedUserList = [...state.selectedPRD2010_R01List];
        // _selectedUserList.push(...(idList.filter(value => !_selectedUserList.includes(value))));
		const _selectedUserList = [...idList];

        return {
            ...state,
            selectedPRD2010_R01List: _selectedUserList
        }
    }),
    removeSelectedPRD2010_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        selectedPRD2010_R01List: [...state.selectedPRD2010_R01List].filter(value => idList ? !idList.includes(value) : false)
    })),
	initSelectedPRD2010_R01List: () => set({ selectedPRD2010_R01List: [] }),
	allSelectedPRD2010_R01List: () => set((state) => ({
		selectedPRD2010_R01List: state.PRD2010_R01List.map(item => item.valueIndex.toString())
	})),

	originalPRD2010_R01List: [],
	
    setOriginalPRD2010_R01List: (PRD2010_R01List: PRD2010_R01Dto[]) => set((state) => {
		let newList: PRD2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		PRD2010_R01List.forEach((item, index) => {
			newList.push({ 
				...defaultPRD2010_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalPRD2010_R01List: newList
		}
	}),

    columnState: null,
    setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),

	allCount: 0,				setAllCount: (allCount: number) => set({ allCount }),
	modalVisible: false,		setModalVisible: (modalVisible: boolean) => set({ modalVisible }),
	modalValueIndex: -1,		setModalValueIndex: (valueIndex: number) => set({ modalValueIndex: valueIndex }),
	modalInstructions: '',		setModalInstructions: (instructions: string) => set({ modalInstructions: instructions }),

	isDisabled: (data: PRD2010_R01Dto) => data.closeTag === 'Y' || data.resultQty > 0,

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),

    initData: () => set((state) => ({
        ...state,
		PRD2010_R01Filter: defaultPRD2010_R01Filter,
		PRD2010_R01PastFilter: defaultPRD2010_R01Filter,
		PRD2010_R01Info: undefined,
        PRD2010_R01List: [],
        selectedPRD2010_R01List: [],
        deletedPRD2010_R01List: [],
		allCount: 0,
		scrollTop: null,
    }))
}));


export const usePRD2010_R01Store_GridCalendar = create<Store_PRD2010_R01_GridCalendarState>((set) => ({

	// ====== GridCalendar 상태 ======
	PRD2010_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined,
		gridCalendarTrigger: null,
	},
	setPRD2010_R01GridCalendarState: (newState: Partial<Store_PRD2010_R01_GridCalendarState['PRD2010_R01GridCalendarState']>) => set((state) => ({
		...state,
		PRD2010_R01GridCalendarState: { ...state.PRD2010_R01GridCalendarState, ...newState }
	})),
	initPRD2010_R01GridCalendarState: () => set((state) => ({
		...state,
		PRD2010_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined,
			gridCalendarTrigger: null,
		}
	})),


})); 